import CloseIcon from '@mui/icons-material/Close';
import { CardActionArea,Dialog,DialogContent,DialogTitle,Divider,IconButton,ImageList,ImageListItem,Skeleton,Stack,Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiError } from "react-icons/bi";
import AttToken from "../../../helpers/attToken";
import Files from "../../../service/Files";
import { logPlanoType } from "../../../types/plano-de-acao/types";
import ModalImagePreview from "../../Reports/Modal/ModalImagePreview";

type props = {
  openLogs: boolean,
  handleOpenLogs: Function,
  logs: logPlanoType[],
};


const filesService = new Files();

const ImagesCmp = ({ images }: { images: string[] }) => {
  const { t } = useTranslation("translation");
  const [image, setImage] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_FILE'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const response = await filesService.downloadFiles(token, 'web', '-', images);
        return response;
      }
    }
  });

  const onLoading = isLoading || isFetching;

  const handleImageError = (imageSrc: string) => {
    setImageErrors(prev => ({ ...prev, [imageSrc]: true }));
  };

  return (
    <>
      {open &&
        <ModalImagePreview
          image={image}
          open={open}
          setOpen={setOpen}
        />}
      <Typography variant='subtitle2' fontSize={13} color='textDisabled' fontWeight={700} component='p'>Fotos:</Typography>
      <ImageList gap={4} cols={3}
        rowHeight={window.location.pathname === '/notificacaoWeb' ? 120 : 150}>
        {onLoading
          ? images?.map((_ele, ind) => {
            return (
              <ImageListItem key={ind}>
                <Skeleton variant="rounded"
                  width={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}
                  height={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'} />;
              </ImageListItem>);
          })
          : <>

            {data?.urls?.map((item: string, i: number) => {
              return (
                <CardActionArea onClick={() => {
                  if (!imageErrors[item]) {
                    setImage(item);
                    setOpen(true);
                  }
                }} key={`imagem ${i}`}>
                  <ImageListItem sx={{ width: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`, height: '150px' }}>
                    {imageErrors[item] ? (
                      <div style={{
                        border: '1px gray solid',
                        borderRadius: 5,
                        width: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`,
                        height: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`,
                      }}>
                        <Stack direction='column' justifyContent='center' alignItems='center' spacing={1.5} color='gray' padding={1}>
                          <BiError size={25} />
                          <Typography color='textDisabled' fontWeight={700} component={'span'} textAlign='center'>
                            {t('users.imageNotFound')}
                          </Typography>
                        </Stack>
                      </div>
                    ) : (
                      <img
                        width={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}
                        height={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}
                        onError={() => handleImageError(item)} // Call handleImageError with the image source
                        style={{
                          width: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`,
                          borderRadius: 2,
                          objectFit: 'cover',
                          maxWidth: '150px',
                          maxHeight: '150px'
                        }}
                        srcSet={`${item}`}
                        src={`${item}`}
                        alt={item}
                        loading="lazy"
                      />
                    )}
                  </ImageListItem>
                </CardActionArea>
              );
            })}
          </>
        }
      </ImageList>
    </>
  );
};

function CardLog({ log }: { log: logPlanoType }) {
  interface LogTarefa {
    usuario: string;
    status: string;
    comentario: string
    data: string;
  }
  const quebraString = (acao: string): LogTarefa | undefined => {
    const regex = /^(?<usuario>.+?) mudou o status da tarefa para (?<status>.+?) com o comentario - ((?<comentario>.*)) as (?<data>.+)$/;
    const match = acao.match(regex);

    if (match && match.groups) {
      const { usuario, status, comentario, data } = match.groups;

      const resultado: LogTarefa = {
        usuario: usuario.trim(),
        status: status.trim(),
        comentario: comentario.trim(),
        data: data.trim(),
      };
      return resultado;
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="h4">
        {quebraString(log.acao)?.usuario}
      </Typography>
      <Stack direction='row' width='100%' justifyContent='space-between' alignItems='flex-start' spacing={1}>
        <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={1} paddingLeft={2}>
          <Typography fontWeight={600} component='p'>Data de atendimento: <Typography component='span'>{quebraString(log.acao)?.data}</Typography></Typography>
          <Typography fontWeight={600} component='p'>Comentário: <Typography component='span'>{quebraString(log.acao)?.comentario}</Typography></Typography>
          <Typography fontWeight={600} component='p'>Status: <Typography component='span'>{quebraString(log.acao)?.status}</Typography></Typography>
        </Stack>
        {log.foto && <ImagesCmp images={[log.foto]} />}
      </Stack>
      <Typography>Atualizado em: {new Date(log.data).toLocaleString('pt-BR')} </Typography>
      <Divider variant="middle" flexItem />
    </Stack>
  );
}

export default function Logs({
  openLogs,
  handleOpenLogs,
  logs,
}: props) {


  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openLogs}
      onClose={() => handleOpenLogs()}
      scroll='paper'
    >
      <DialogTitle>Logs</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleOpenLogs()}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        {logs?.map((log, index) => (
          <CardLog key={index} log={log} />
        ))}
      </DialogContent>
    </Dialog>
  );
}
